import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Network, Bot, HardHat, PenTool, Cpu, UserCog, GraduationCap, CheckCircle2, Circle,
  SlidersHorizontal, X, FileText, Wrench, Loader2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { Card, CardHeader, Label } from "@/components/common";

const BRANCHES = [
  { key: "owner_support", label: "Owner Support", icon: UserCog, desc: "Keeps the ADHD, high-output owner on top of everything — catches what's forgotten, routes messages, escalates." },
  { key: "design", label: "Design Team", icon: PenTool, desc: "Runs design jobs start→finish. Work is split so no agent is overloaded with tools/files it can't use well." },
  { key: "field", label: "Field Crew", icon: HardHat, desc: "Takes the design package into construction: CPM, shop drawings, materials, subs, PM/superintendent docs." },
  { key: "shared", label: "Shared Core", icon: Cpu, desc: "Cross-cutting orchestration: intake, records, files, approvals, QA, strategy." },
  { key: "company", label: "Domain Specialists", icon: Bot, desc: "Per-company domain agents." },
];
const ROLE_STATUS = { needed: "bg-st-critical/10 text-st-critical", recommended: "bg-st-warning/15 text-st-warning", optional: "bg-surface-muted text-ink-muted" };
const TRAIN_STATUS = { ready: "text-st-success", draft: "text-st-warning" };
const ALL_TAGS = ["design", "interior", "structural", "schedule", "pricing", "subcontractor", "cabinet", "digital"];

export default function Org() {
  const { companyParam, companyId } = useApp();
  const [editAgent, setEditAgent] = useState(null);
  const { data: agents = [] } = useQuery({ queryKey: ["agents", companyId], queryFn: async () => (await api.get("/agents", { params: companyParam })).data });
  const { data: roles = [] } = useQuery({ queryKey: ["human-roles"], queryFn: async () => (await api.get("/human-roles")).data });
  const byBranch = (b) => agents.filter((a) => (a.branch || a.scope) === b);

  return (
    <div data-testid="org-page">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.12em] uppercase text-ink-muted"><Network className="w-3.5 h-3.5 text-brand" /> Company Org</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-ink mt-1">Reverse-engineered, agent by agent.</h1>
        <p className="text-sm text-ink-muted mt-1">Each agent is locked to a tight <b>knowledge scope</b> — only the files & tools for its lane — so nothing gets glossed over. Tune any scope; the orchestrator must respect it.</p>
      </div>

      <div className="space-y-4">
        {BRANCHES.map((br) => {
          const list = byBranch(br.key);
          if (list.length === 0) return null;
          return (
            <Card key={br.key} testId={`branch-${br.key}`}>
              <div className="flex items-start gap-3 px-4 py-3 border-b border-border">
                <div className="w-9 h-9 bg-ink text-white flex items-center justify-center shrink-0"><br.icon className="w-5 h-5" /></div>
                <div className="flex-1"><h3 className="font-heading text-base font-semibold text-ink">{br.label}</h3><p className="text-xs text-ink-muted">{br.desc}</p></div>
                <span className="text-[11px] font-mono text-ink-faint">{list.length} agents</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 divide-y md:divide-y-0 divide-border">
                {list.map((a) => {
                  const sc = a.knowledge_scope || {};
                  return (
                    <div key={a.id} className="p-3 border-border flex flex-col" data-testid={`org-agent-${a.key}`}>
                      <div className="flex items-start gap-2">
                        <Bot className="w-3.5 h-3.5 text-brand shrink-0 mt-0.5" />
                        <span className="text-sm font-semibold text-ink leading-tight flex-1">{a.name}</span>
                        <button onClick={() => setEditAgent(a)} data-testid={`edit-scope-${a.key}`} title="Tune knowledge scope"
                          className="text-ink-faint hover:text-brand"><SlidersHorizontal className="w-3.5 h-3.5" /></button>
                      </div>
                      <p className="text-[11px] text-ink-muted mt-1 leading-snug flex-1">{a.desc}</p>
                      <div className="mt-2 pt-2 border-t border-border flex flex-wrap items-center gap-1">
                        <span className="text-[10px] font-mono text-ink-faint flex items-center gap-1"><FileText className="w-3 h-3" /> {(sc.file_tags || []).join(", ") || "—"}</span>
                        <span className="text-[10px] font-mono text-ink-faint flex items-center gap-1 ml-1"><Wrench className="w-3 h-3" /> {(sc.tools || []).length}</span>
                        <span className="text-[10px] font-mono text-ink-faint ml-auto">cap {sc.max_context_items ?? "—"}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          );
        })}
      </div>

      <Card className="mt-4" testId="human-roles-panel">
        <CardHeader title="Human Roles & Training Readiness" icon={GraduationCap} />
        <div className="divide-y divide-border">
          {roles.map((r) => (
            <div key={r.id} className="px-4 py-3" data-testid={`human-role-${r.id}`}>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-ink flex-1">{r.title}</span>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${ROLE_STATUS[r.status] || ROLE_STATUS.optional}`}>{r.status}</span>
                <span className={`text-[11px] font-semibold flex items-center gap-1 ${TRAIN_STATUS[r.training_program] || "text-ink-faint"}`}>
                  {r.training_program === "ready" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />} training {r.training_program}
                </span>
              </div>
              <p className="text-xs text-ink-muted mt-0.5">{r.purpose}</p>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {(r.training_outline || []).map((t) => <span key={t} className="bg-surface-muted px-1.5 py-0.5 text-[10px] text-ink-muted">{t}</span>)}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {editAgent && <ScopeEditor agent={editAgent} onClose={() => setEditAgent(null)} />}
    </div>
  );
}

function ScopeEditor({ agent, onClose }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canEdit = user?.role === "owner" || user?.role === "admin";
  const [busy, setBusy] = useState(false);
  const { data: tools = [] } = useQuery({ queryKey: ["tools"], queryFn: async () => (await api.get("/tools")).data });
  const { data: scope } = useQuery({ queryKey: ["scope", agent.id], queryFn: async () => (await api.get(`/agents/${agent.id}/scope`)).data });
  const [tags, setTags] = useState(agent.knowledge_scope?.file_tags || []);
  const [tls, setTls] = useState(agent.knowledge_scope?.tools || []);
  const [cap, setCap] = useState(agent.knowledge_scope?.max_context_items || 8);

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/agents/${agent.id}/scope`, { file_tags: tags, tools: tls, max_context_items: Number(cap) });
      qc.invalidateQueries({ queryKey: ["agents"] });
      qc.invalidateQueries({ queryKey: ["scope", agent.id] });
      toast.success("Knowledge scope updated — orchestrator will respect it.");
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-white border-2 border-brand max-h-[90vh] overflow-y-auto" data-testid="scope-editor">
        <div className="flex items-center justify-between px-5 py-3 bg-ink text-white">
          <div><div className="text-[10px] uppercase tracking-wider text-white/50">Knowledge Scope</div><h2 className="font-heading font-bold">{agent.name}</h2></div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <Label className="mb-2">File lanes (tags this agent may read)</Label>
            <div className="flex flex-wrap gap-1.5">
              {ALL_TAGS.map((t) => (
                <button key={t} disabled={!canEdit} onClick={() => toggle(tags, setTags, t)}
                  className={`px-2 py-1 text-xs border ${tags.includes(t) ? "bg-brand text-white border-brand" : "border-border text-ink-muted hover:border-ink"}`}>{t}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="mb-2">Tools / plugins</Label>
            <div className="grid grid-cols-2 gap-1.5">
              {tools.map((t) => (
                <button key={t.key} disabled={!canEdit} onClick={() => toggle(tls, setTls, t.key)}
                  className={`px-2 py-1 text-xs border text-left ${tls.includes(t.key) ? "bg-brand/10 text-brand border-brand" : "border-border text-ink-muted hover:border-ink"}`}>{t.label}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="mb-1">Max context items (hard cap)</Label>
            <input type="number" min={1} max={50} value={cap} disabled={!canEdit} onChange={(e) => setCap(e.target.value)}
              className="w-24 border border-border px-3 py-1.5 text-sm" data-testid="scope-cap-input" />
            {scope && <span className="ml-3 text-xs text-ink-muted">{scope.available_count} file(s) match · {scope.capped ? `capped to ${cap}` : "all within cap"}</span>}
          </div>
          {!canEdit && <div className="text-xs text-ink-muted">Only the Owner can change scopes.</div>}
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-border">Close</button>
            {canEdit && <button onClick={save} disabled={busy} data-testid="save-scope-btn" className="flex items-center gap-2 bg-brand text-white px-4 py-2 text-sm font-medium disabled:opacity-60">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Save Scope</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
